/**
 * @deprecated This hook is deprecated and will be removed in a future version.
 * 
 * Please migrate to hooks from `@/hooks/queries/useAssessmentQueries.ts`:
 * 
 * | Old Pattern                    | New Pattern                              |
 * |-------------------------------|------------------------------------------|
 * | useAssessments().assessments  | useAssessments() from useAssessmentQueries |
 * | saveAssessment()              | useSaveAssessment() mutation              |
 * | deleteAssessment()            | useDeleteAssessment() mutation            |
 * | updateAssessmentProfile()     | useUpdateAssessment() mutation            |
 * | hasTPSAssessment              | useHasTPSAssessment() hook               |
 * 
 * Benefits of React Query hooks:
 * - Automatic caching and cache invalidation
 * - Cross-feature cache sync (liker, quizzes, insights)
 * - Optimistic updates
 * - Better loading/error state management
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { PersonalityProfile } from '@/types/tps.types';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/utils/structuredLogging';
import { migrateProfileToNewFormat } from '@/utils/profileMigration';
import { archiveAssessmentResponses } from '@/hooks/useResponseArchive';
import { useEffectiveUser } from '@/hooks/useEffectiveUser';

// Log deprecation warning in development only
if (import.meta.env.DEV) {
  console.warn(
    '[DEPRECATED] useAssessments hook from src/hooks/useAssessments.ts is deprecated. ' +
    'Please migrate to @/hooks/queries/useAssessmentQueries.ts'
  );
}

// Validate and normalize profile structure before saving
const validateAndNormalizeProfile = (profile: PersonalityProfile): PersonalityProfile => {
  const normalized: PersonalityProfile = {
    dominantTraits: profile.dominantTraits || {},
    traitScores: profile.traitScores || {},
    domainScores: profile.domainScores || {
      External: 0,
      Internal: 0,
      Interpersonal: 0,
      Processing: 0,
    },
    mappings: {
      mbti: profile.mappings?.mbti || '',
      enneagram: profile.mappings?.enneagram || '',
      enneagramDetails: profile.mappings?.enneagramDetails || { type: 0, wing: 0, tritype: '' },
      bigFive: profile.mappings?.bigFive || {},
      dndAlignment: profile.mappings?.dndAlignment || '',
      socionics: profile.mappings?.socionics || '',
      hollandCode: profile.mappings?.hollandCode || '',
      personalityMatches: profile.mappings?.personalityMatches || [],
      integralDetail: profile.mappings?.integralDetail,
      // Core framework details (preserve from source)
      mbtiDetail: profile.mappings?.mbtiDetail,
      enneagramDetail: profile.mappings?.enneagramDetail,
      bigFiveDetail: profile.mappings?.bigFiveDetail,
      sloanDetail: profile.mappings?.sloanDetail,
      alignmentDetail: profile.mappings?.alignmentDetail,
      hollandDetail: profile.mappings?.hollandDetail,
      attachmentStyle: profile.mappings?.attachmentStyle,
      attachmentDetail: profile.mappings?.attachmentDetail,
      socionicsDetail: profile.mappings?.socionicsDetail,
      discDetail: profile.mappings?.discDetail,
      disc: profile.mappings?.disc,
      sloan: profile.mappings?.sloan,
      // Extended frameworks (preserve from source)
      hogwartsHouse: profile.mappings?.hogwartsHouse,
      hogwartsDetail: profile.mappings?.hogwartsDetail,
      loveLanguage: profile.mappings?.loveLanguage,
      loveLanguageDetail: profile.mappings?.loveLanguageDetail,
      trueColor: profile.mappings?.trueColor,
      trueColorDetail: profile.mappings?.trueColorDetail,
      classicalTemperament: profile.mappings?.classicalTemperament,
      temperamentDetail: profile.mappings?.temperamentDetail,
      keirsey: profile.mappings?.keirsey,
      keirseyDetail: profile.mappings?.keirseyDetail,
    },
    timestamp: profile.timestamp || new Date().toISOString(),
  };

  logger.info('Profile validated and normalized', {
    component: 'useAssessments',
    action: 'validateAndNormalizeProfile',
    metadata: {
      hasMappings: !!profile.mappings,
      hasBigFive: !!profile.mappings?.bigFive,
      hasIntegral: !!profile.mappings?.integralDetail
    }
  });

  return normalized;
};

// Assessment interface with version detection
interface Assessment {
  id: string;
  variant: string;
  profile: PersonalityProfile;
  created_at: string;
  updated_at: string;
  user_id: string;
  responses: number[] | Record<string, number> | null;
  version?: 'v2' | 'v3'; // Detected version
  is_preliminary?: boolean;
  admin_created?: boolean;
  source_type?: string;
  evolution_plan?: any;
  xp_earned?: number;
}

export type AssessmentType = 'tps' | 'integral' | 'unknown';

/**
 * Detect assessment version based on responses format
 */
function detectAssessmentVersion(responses: number[] | Record<string, number> | null): 'v2' | 'v3' {
  if (!responses) return 'v2'; // Default for null
  if (Array.isArray(responses)) {
    return 'v2';
  } else if (typeof responses === 'object' && responses !== null) {
    return 'v3';
  }
  return 'v2'; // Default to v2 for safety
}

/**
 * Detect assessment type based on profile data
 */
export function getAssessmentType(profile: PersonalityProfile | null): AssessmentType {
  if (!profile) return 'unknown';
  if (profile.v3Metadata || profile.domainScores) return 'tps';
  if (profile.mappings?.integralDetail) return 'integral';
  return 'unknown';
}

export function useAssessments() {
  const { effectiveUserId, isImpersonating, authLoading } = useEffectiveUser();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true); // Start as true to prevent flash of empty state

  const saveAssessment = async (
    profile: PersonalityProfile,
    responses: number[] | Record<string, number>,
    variant: string = 'tps'
  ): Promise<Assessment | undefined> => {
    // Block saves when impersonating
    if (isImpersonating) {
      toast({
        title: "Read-only Mode",
        description: "Cannot save assessments while viewing as another user",
        variant: "destructive"
      });
      return;
    }

    try {
      // Validate and normalize profile structure before saving
      const normalizedProfile = validateAndNormalizeProfile(profile);
      const version = detectAssessmentVersion(responses);

      const { data, error } = await supabase
        .from('assessments')
        .insert({
          user_id: (await supabase.auth.getUser()).data.user!.id,
          variant,
          responses: responses as unknown as Json,
          profile: normalizedProfile as unknown as Json
        })
        .select()
        .single();

      if (error) throw error;

      const newAssessment: Assessment = {
        ...data,
        profile: data.profile as unknown as PersonalityProfile,
        responses: responses,
        version
      };

      // Archive responses for future analysis and re-scoring
      const testType = variant === 'integral' ? 'integral' : 'tps';
      archiveAssessmentResponses({
        userId: data.user_id,
        assessmentId: data.id,
        responses,
        testType,
        completedAt: data.created_at,
      }).catch(archiveError => {
        // Don't fail the save if archiving fails
        logger.warn('Failed to archive responses', {
          component: 'useAssessments',
          action: 'archiveResponses',
        });
      });

      logger.info('Assessment saved successfully', {
        component: 'useAssessments',
        action: 'saveAssessment',
        metadata: { variant, version, hasResponses: responses ? (Array.isArray(responses) ? responses.length : Object.keys(responses).length) : 0 }
      });

      toast({
        title: "Assessment Saved",
        description: "Your personality profile has been saved to your account.",
      });

      await loadAssessments();
      return newAssessment;
    } catch (error: unknown) {
      logger.error('Error saving assessment', { 
        component: 'useAssessments', 
        action: 'saveAssessment' 
      }, error as Error);
      toast({
        title: "Save Failed",
        description: error instanceof Error ? error.message : "Failed to save assessment. Please try again.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const loadAssessments = async (attempt = 0) => {
    if (attempt === 0) setLoading(true);
    let retrying = false;
    try {
      // Use effective user ID (impersonated user if active)
      if (!effectiveUserId) {
        setAssessments([]);
        setLoading(false);
        return;
      }

      // Fetch assessments for the effective user
      // Exclude integral_session records (temporary progress data, not real assessments)
      const { data, error } = await supabase
        .from('assessments')
        .select('*')
        .eq('user_id', effectiveUserId)
        .neq('source_type', 'integral_session')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const assessmentsWithVersion = (data || []).map(item => ({
        ...item,
        profile: migrateProfileToNewFormat(item.profile as any),
        responses: item.responses as unknown as number[] | Record<string, number> | null,
        version: detectAssessmentVersion(item.responses as unknown as number[] | Record<string, number> | null)
      }));
      
      setAssessments(assessmentsWithVersion);
    } catch (error: unknown) {
      logger.error('Error loading assessments', { 
        component: 'useAssessments', 
        action: 'loadAssessments' 
      }, error as Error);
      // A freshly restored or refreshed mobile session can briefly make the
      // first authenticated request without a usable token. Refresh once and
      // retry with backoff before presenting a real failure to the user.
      if (attempt < 2) {
        retrying = true;
        if (attempt === 0) {
          await supabase.auth.refreshSession().catch(() => undefined);
        }
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
        return loadAssessments(attempt + 1);
      }
      toast({
        title: "Couldn't load your personality",
        description: "We had trouble reaching your results. Pull to refresh or try again in a moment.",
        variant: "destructive"
      });
    } finally {
      if (!retrying) setLoading(false);
    }
  };

  const deleteAssessment = async (id: string) => {
    // Block deletes when impersonating
    if (isImpersonating) {
      toast({
        title: "Read-only Mode",
        description: "Cannot delete assessments while viewing as another user",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('assessments')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Assessment Deleted",
        description: "The assessment has been removed from your account.",
      });

      await loadAssessments();
    } catch (error: unknown) {
      logger.error('Error deleting assessment', { 
        component: 'useAssessments', 
        action: 'deleteAssessment' 
      }, error as Error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete assessment. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Reload assessments when effective user changes (impersonation start/end)
  useEffect(() => {
    if (authLoading) return;
    if (effectiveUserId) {
      loadAssessments();
    } else {
      setAssessments([]);
      setLoading(false);
    }
  }, [effectiveUserId, authLoading]);

  // Also listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setAssessments([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const updateAssessmentProfile = async (assessmentId: string, profile: PersonalityProfile) => {
    // Block updates when impersonating
    if (isImpersonating) {
      toast({
        title: "Read-only Mode",
        description: "Cannot update assessments while viewing as another user",
        variant: "destructive"
      });
      return;
    }

    try {
      // Validate and normalize profile structure before updating
      const normalizedProfile = validateAndNormalizeProfile(profile);

      const { error } = await supabase
        .from('assessments')
        .update({ profile: normalizedProfile as unknown as Json })
        .eq('id', assessmentId);

      if (error) throw error;

      logger.info('Assessment profile updated successfully', {
        component: 'useAssessments',
        action: 'updateAssessmentProfile',
        metadata: { assessmentId }
      });

      // Update the local state with normalized profile
      setAssessments(prev => prev.map(assessment => 
        assessment.id === assessmentId 
          ? { ...assessment, profile: normalizedProfile }
          : assessment
      ));
    } catch (error: unknown) {
      logger.error('Error updating assessment profile', { 
        component: 'useAssessments', 
        action: 'updateAssessmentProfile' 
      }, error as Error);
      toast({
        title: "Update Failed",
        description: "Failed to update assessment profile.",
        variant: "destructive"
      });
    }
  };

  // Check if user has any TPS-compatible assessments
  const hasTPSAssessment = assessments.some(a => 
    a.profile?.v3Metadata || a.profile?.domainScores
  );

  return {
    assessments,
    loading,
    saveAssessment,
    loadAssessments,
    deleteAssessment,
    updateAssessmentProfile,
    hasTPSAssessment,
    isImpersonating
  };
}
